import {funeralAllowed,funeralNoticeMessage,validateFuneralBasket} from './welch-funeral-notice.js';
/** Build real, priced cart lines; gift text never changes a Shopify price. */
export function buildGiftPayload(formData, selections, card, title, groupId) {
  const id = String(formData.get('id') || '');
  const quantity = Number(formData.get('quantity') || 1);
  if (!/^\d+$/.test(id) || !Number.isInteger(quantity) || quantity < 1) throw new Error('Please choose an available size and quantity.');
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match && typeof value === 'string' && value.trim()) properties[match[1]] = value;
  }
  if (properties['Card message preference'] === 'No card message requested') {
    delete properties['Card message'];
  }
  // Native iPhone date controls submit ISO; preserve the order's UK date format.
  const funeralDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(properties['Funeral date'] || '');
  if (funeralDate) properties['Funeral date'] = `${funeralDate[3]}/${funeralDate[2]}/${funeralDate[1]}`;
  validateFuneralBasket({items:[{properties}]});
  properties._welch_gift_group = groupId;
  if (card) properties.Card = card;
  const main = { id, quantity, properties };
  if (formData.get('selling_plan')) main.selling_plan = String(formData.get('selling_plan'));
  const seen = new Set([id]);
  const addons = [];
  for (const selection of selections) {
    if (!/^\d+$/.test(selection.id) || seen.has(selection.id)) continue;
    seen.add(selection.id);
    const addonProperties = { 'For bouquet': title, _welch_gift_group: groupId };
    if (selection.ribbon) {
      const wording = String(selection.wording || '').toUpperCase();
      if (!wording.trim() || wording.length > 12) throw new Error('Please enter ribbon wording of up to 12 characters, including spaces.');
      addonProperties['Ribbon wording'] = wording;
    }
    addons.push({ id: selection.id, quantity, parent_id: id, properties: addonProperties });
  }
  return { items: [main, ...addons], sections: formData.get('sections') || '', sections_url: formData.get('sections_url') || window.location.pathname };
}

export class WelchGifting extends HTMLElement {
  busy = false;
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-occasion]')) this.updateOccasion();
      if(event.target.matches('[data-gift-variant]')) { for(const input of this.querySelectorAll('[data-gift-variant]')) if(input.value===event.target.value && !input.disabled) input.checked=event.target.checked; }
      this.validateFuneralNotice();
      this.updateCount();
      this.updateSummary();
    });
    this.addEventListener('input', () => { this.updateCount(); this.validateFuneralName(); this.validateFuneralNotice(); this.updateRibbon(); });
    this.addEventListener('click', event => {
      if(event.target.closest('[data-all-addons]')) { this.querySelector('[data-addons-dialog]').showModal(); void this.loadAllAddons(); }
      if(event.target.closest('[data-close-addons]')) this.querySelector('[data-addons-dialog]').close();
      if(event.target.closest('[data-retry-addons]')) void this.loadAllAddons();
    });
    this.updateOccasion();
    this.updateCount();
  }
  async loadAllAddons() {
    if(this.loadingAddons || this.addonsLoaded) return;
    this.loadingAddons=true;
    const status=this.querySelector('[data-addons-status]'), retry=this.querySelector('[data-retry-addons]');
    retry.hidden=true; status.textContent='Loading all add-ons…';
    try {
      let page=this.nextAddonPage||1;
      while(page) {
        const url=new URL(`${window.Shopify?.routes?.root||'/'}collections/all`,window.location.origin);
        url.searchParams.set('view','welch-addons');url.searchParams.set('page',String(page));
        const response=await fetch(url);if(!response.ok)throw Error('load');
        const data=await response.json();if(typeof data.html!=='string'||(data.next!==null&&(!Number.isInteger(data.next)||data.next<=page)))throw Error('data');
        if(!this.isConnected)return;
        const content=document.createElement('template');content.innerHTML=data.html;
        const container=this.querySelector('[data-all-addon-choices]');
        for(const tile of content.content.querySelectorAll('.welch-gifting__choice')) {
          const input=tile.querySelector('[data-gift-variant]');if(!input)continue;
          if([...container.querySelectorAll('[data-gift-variant]')].some(i=>i.value===input.value))continue;
          const existing=[...new Map([...this.querySelectorAll('[data-gift-variant]:checked:not(:disabled)')].map(input=>[input.value,input])).values()].find(i=>i.value===input.value);
          input.disabled=!this.querySelector('[data-occasion]')?.value||input.hasAttribute('data-unavailable');input.checked=Boolean(existing);
          container.append(tile);
        }
        page=data.next;this.nextAddonPage=page;
      }
      this.addonsLoaded=true;status.textContent=this.querySelector('[data-all-addon-choices]').children.length?'Choose your extras, then select Done.':'No add-ons are currently available.';
    } catch {status.textContent='Some add-ons could not be loaded. Please retry.';retry.hidden=false;}
    finally {this.loadingAddons=false;}
  }
  updateOccasion() {
    const slot = this.querySelector('[data-occasion]')?.selectedOptions[0]?.dataset.slot;
    for (const panel of this.querySelectorAll('[data-occasion-panel]')) {
      const active = panel.dataset.occasionPanel === slot;
      panel.hidden = !active;
      for (const input of panel.querySelectorAll('input')) {
        input.disabled = !active || input.hasAttribute('data-unavailable');
        // Never retain invisible extras when the occasion changes.
        input.checked = input.hasAttribute('data-default-card');
      }
    }
    this.querySelector('[data-addons-dialog]')?.close();
    for(const input of this.querySelectorAll('[data-all-addon-choices] input')) {input.checked=false;input.disabled=!slot||input.hasAttribute('data-unavailable');}
    const funeral = this.querySelector('[data-funeral-details]');
    const activeFuneral = this.querySelector('[data-occasion]')?.value.trim().toLowerCase() === 'funeral';
    if (funeral) {
      funeral.hidden = !activeFuneral;
      if (!activeFuneral) funeral.querySelector('welch-date-picker')?.close?.();
      for (const field of funeral.querySelectorAll('input, select')) {
        field.disabled = !activeFuneral;
        field.required = activeFuneral;
      }
      this.validateFuneralName();
    }
    this.updateSummary();
  }
  validateFuneralNotice() {
    const picker=this.querySelector('[data-funeral-notice]'),time=this.querySelector('[name="properties[Funeral time]"]'),date=picker?.querySelector('input');
    if(!date||!time)return;
    for(const option of time.options)if(option.value)option.disabled=!date.disabled&&Boolean(date.value)&&!funeralAllowed(date.value,option.value);
    time.setCustomValidity(!time.disabled&&date.value&&time.value&&!funeralAllowed(date.value,time.value)?funeralNoticeMessage:'');
    picker.validate?.();
  }
  validateFuneralName() {
    const name = this.querySelector('[data-funeral-name]');
    if (name) name.setCustomValidity(!name.disabled && !name.value.trim() ? 'Please enter the deceased’s name.' : '');
  }
  updateCount() {
    const message = this.querySelector('[data-message]');
    const noMessage = this.querySelector('[data-no-message]').checked;
    message.disabled = noMessage;
    message.required = message.dataset.messageRequired === 'true' && !noMessage;
    message.setCustomValidity(message.required && !message.value.trim() ? 'Please add a message for the recipient, and remember to include who it’s from, or select ‘I don’t want to include a card message’.' : '');
    this.querySelector('[data-message-count]').textContent = `${message.value.length} / ${message.maxLength}`;
  }
  updateRibbon() {
    const fields = this.querySelector('[data-ribbon-fields]');
    if (!fields) return;
    const selected = this.querySelector('[data-funeral-ribbon]:checked:not(:disabled)');
    const input = fields.querySelector('[data-ribbon-wording]');
    const uppercase = input.value.toUpperCase();
    if (input.value !== uppercase) {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const before = input.value;
      input.value = uppercase;
      if (start !== null && end !== null) {
        input.setSelectionRange(before.slice(0, start).toUpperCase().length, before.slice(0, end).toUpperCase().length);
      }
    }
    fields.hidden = !selected;
    input.disabled = !selected;
    input.required = Boolean(selected);
    input.setCustomValidity(selected && (!input.value.trim() || input.value.length > 12) ? 'Please enter ribbon wording of up to 12 characters, including spaces.' : '');
    fields.querySelector('[data-ribbon-count]').textContent = `${input.value.length} / 12`;
  }
  updateSummary() {
    this.updateRibbon();
    const summary = this.querySelector('[data-selection]');
    if (!summary) return;
    const selected = [...new Map([...this.querySelectorAll('[data-gift-variant]:checked:not(:disabled)')].map(input=>[input.value,input])).values()];
    summary.textContent = selected.length ? `Selected extras (per bouquet): ${selected.map(input => `${input.dataset.giftLabel} (+ ${input.dataset.priceLabel})`).join('; ')}` : '';
  }
  showError(message) {
    const error = this.querySelector('[data-gift-error]');
    error.textContent = message;
    error.hidden = !message;
  }
  createPayload(formData) {
    const selected = [...new Map([...this.querySelectorAll('[data-gift-variant]:checked:not(:disabled)')].map(input=>[input.value,input])).values()].map(input => ({ id: input.value, ribbon: input.hasAttribute('data-funeral-ribbon'), wording: this.querySelector('[data-ribbon-wording]')?.value || '' }));
    // Paid cards are represented by their cart line. Do not leave a stale paid
    // card instruction on the bouquet if the customer later removes that line.
    const cardInput = this.querySelector('[data-card-label]:checked:not(:disabled)');
    const card = cardInput && !cardInput.hasAttribute('data-gift-variant') ? cardInput.dataset.cardLabel : '';
    return buildGiftPayload(formData, selected, card, this.dataset.productTitle, crypto.randomUUID());
  }
}
if (!customElements.get('welch-gifting')) customElements.define('welch-gifting', WelchGifting);
